import type { FormDefinition, FormSubmission, AuditEntry } from '@/types/cgt'

const PROCESSING_DEF_ID = 'def-processing-title21-001'
const PROCESSING_SUB_ID = 'sub-processing-title21-001'

const processingDefinition: FormDefinition = {
  id: PROCESSING_DEF_ID,
  name: 'Title21 Processing Worksheet',
  category: 'production',
  formType: 'processing',
  version: 1,
  predecessorFormTypes: ['collection'],
  createdBy: 'admin',
  createdAt: '2026-06-18T00:00:00.000Z',
  updatedAt: '2026-06-18T00:00:00.000Z',
  sections: [{
    id: 'sec-processing',
    title: '',
    width: 'full',
    order: 0,
    fields: [
    { id: 'recipient-name', label: 'Recipient Name', fieldType: 'text', required: true, order: 0 },
    { id: 'recipient-mrn', label: 'Recipient MRN', fieldType: 'text', required: true, order: 1 },
    { id: 'recipient-dob', label: 'Recipient DOB', fieldType: 'date', required: true, order: 2 },
    { id: 'collection-start-time', label: 'Collection Start', fieldType: 'datetime', required: false, order: 2, helpText: 'Stored as YYYY-MM-DDTHH:mm (naive local time, no timezone)' },
    { id: 'recipient-gender', label: 'Recipient Gender', fieldType: 'dropdown', required: true, order: 3, options: ['Male', 'Female', 'Other'] },
    { id: 'recipient-abo-rh', label: 'Recipient ABO/Rh', fieldType: 'text', required: true, order: 4 },
    { id: 'recipient-weight-kg', label: 'Recipient Weight (kg)', fieldType: 'number', required: true, order: 5 },
    { id: 'diagnosis', label: 'Diagnosis', fieldType: 'text', required: true, order: 6 },
    { id: 'transplant-physician', label: 'Transplant Physician', fieldType: 'text', required: false, order: 7 },
    { id: 'donor-type', label: 'Donor Type', fieldType: 'dropdown', required: true, order: 8, options: ['Autologous', 'Allogeneic Related', 'Allogeneic Unrelated'] },
    { id: 'product-description', label: 'Product Description', fieldType: 'text', required: true, order: 9 },
    { id: 'din', label: 'DIN', fieldType: 'text', required: true, order: 10 },
    { id: 'product-code', label: 'Product Code', fieldType: 'text', required: false, order: 11 },
    { id: 'biohazard', label: 'Biohazard', fieldType: 'dropdown', required: true, order: 12, options: ['Yes', 'No'] },
    { id: 'third-party', label: '3rd Party', fieldType: 'dropdown', required: true, order: 13, options: ['Yes', 'No'] },
    { id: 'other-additives', label: 'Other Additives', fieldType: 'dropdown', required: true, order: 14, options: ['Yes', 'No'] },
    { id: 'cryopreserved', label: 'Cryopreserved', fieldType: 'dropdown', required: true, order: 15, options: ['Yes', 'No'] },
    { id: 'mobilized', label: 'Mobilized', fieldType: 'dropdown', required: true, order: 16, options: ['Yes', 'No'] },
    { id: 'required-processing', label: 'Required Processing', fieldType: 'textarea', required: true, order: 17 },
    { id: 'volume-ml', label: 'Volume (mL)', fieldType: 'number', required: false, order: 18 },
    { id: 'tnc-ml-x10e6', label: 'TNC/mL x10^6', fieldType: 'number', required: false, order: 19 },
    { id: 'tnc-x10e10', label: 'TNC x10^10', fieldType: 'number', required: false, order: 20 },
    { id: 'weight-x10e8', label: 'Wt x10^8', fieldType: 'number', required: false, order: 21 },
    { id: 'viable-cd34-percent', label: 'vCD34% of vCD45%', fieldType: 'number', required: false, order: 22 },
    { id: 'cd34-via-absmcl', label: 'CD34 Via Abs/mcL', fieldType: 'number', required: false, order: 23 },
    { id: 'viable-cd34-x10e6', label: 'Viable CD34 x10^6', fieldType: 'number', required: false, order: 24 },
    { id: 'cd34-kg-x10e6', label: 'CD34/kg x10^6', fieldType: 'number', required: false, order: 25 },
    { id: 'cd45-via-viability', label: 'CD45 Via Viability', fieldType: 'number', required: false, order: 26 },
    { id: 'cd45-via-absmcl', label: 'CD45 Via Abs/mcL', fieldType: 'number', required: false, order: 27 },
    { id: 'viable-cd45-x10e6', label: 'Viable CD45 x10^6', fieldType: 'number', required: false, order: 28 },
    { id: 'vcd3-percent-vcd45', label: 'vCD3% of vCD45%', fieldType: 'number', required: false, order: 29 },
    { id: 'cd3-via-absmcl', label: 'CD3 Via Abs/mcL', fieldType: 'number', required: false, order: 30 },
    { id: 'viable-cd3-x10e6', label: 'Viable CD3 x10^6', fieldType: 'number', required: false, order: 31 },
    { id: 'cd3-kg-x10e6', label: 'CD3/kg x10^6', fieldType: 'number', required: false, order: 32 },
    { id: 'cd34-recovery-percent', label: 'CD34 Recovery (%)', fieldType: 'number', required: false, order: 33 },
    { id: 'cd3-log-red', label: 'CD3 Log Red (#)', fieldType: 'number', required: false, order: 34 },
    { id: 'fresh-aliquot-label', label: 'Aliquot Fresh Label', fieldType: 'text', required: false, order: 35 },
    { id: 'fresh-aliquot-din', label: 'Aliquot Fresh DIN', fieldType: 'text', required: false, order: 36 },
    { id: 'fresh-product-code', label: 'Aliquot Fresh Product Code', fieldType: 'text', required: false, order: 37 },
    { id: 'cryo-aliquot-label', label: 'Aliquot Cryo Label', fieldType: 'text', required: false, order: 38 },
    { id: 'cryo-aliquot-din', label: 'Aliquot Cryo DIN', fieldType: 'text', required: false, order: 39 },
    { id: 'cryo-product-code', label: 'Aliquot Cryo Product Code', fieldType: 'text', required: false, order: 40 },
    {
      id: 'medical-director-review',
      label: 'Medical Director Review',
      fieldType: 'signature',
      required: true,
      order: 41,
      requiredGroup: 'MedicalDirector',
    },
    ],
  }],
}

export const mockDefinitions: FormDefinition[] = [processingDefinition]

export const mockSubmissions: FormSubmission[] = [
  {
    id: PROCESSING_SUB_ID,
    formDefinitionId: PROCESSING_DEF_ID,
    formType: 'processing',
    patientMrn: '123456789',
    status: 'draft',
    values: {
      'recipient-name': 'CD34_Test',
      'recipient-mrn': '123456789',
      'recipient-dob': '1995-03-01',
      'recipient-gender': 'Male',
      'recipient-abo-rh': 'A Negative',
      'recipient-weight-kg': 70,
      diagnosis: 'Chronic lymphocytic leukemia',
      'transplant-physician': 'Transplant Physician',
      'donor-type': 'Allogeneic Unrelated',
      'product-description': 'HPC, Apheresis',
      din: 'W409625100106',
      'product-code': 'S13824/B',
      biohazard: 'No',
      'third-party': 'Yes',
      'other-additives': 'Yes',
      cryopreserved: 'Yes',
      mobilized: 'Yes',
      'required-processing': 'Volume Adjustment; Division; CD34 Selection; DI; Pre-Cryo; Cryo',
      'volume-ml': 446,
      'tnc-ml-x10e6': 250,
      'tnc-x10e10': 1115,
      'weight-x10e8': 1593,
      'viable-cd34-percent': 0.82,
      'viable-cd34-x10e6': 914.3,
      'cd34-kg-x10e6': 13.06,
      'cd45-via-viability': 100,
      'vcd3-percent-vcd45': 45.59,
      'viable-cd3-x10e6': 50832.85,
      'cd3-kg-x10e6': 726.18,
      'fresh-aliquot-label': 'InvtmiD',
      'fresh-aliquot-din': 'W409625100106',
      'fresh-product-code': 'Product Code',
      'cryo-aliquot-label': 'S13824/B0',
      'cryo-aliquot-din': 'W409625100106',
      'cryo-product-code': 'HPC, APHERESIS',
    },
    createdAt: '2026-06-18T08:00:00.000Z',
    updatedAt: '2026-06-18T08:00:00.000Z',
  },
]

export const mockAuditEntries: AuditEntry[] = []
